import { EMessageV2Status } from '@cian/chats-api';
import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLastMessageName, usePreviewAvatarNode } from './hooks';
import {
  trackOpenChatListItemClick,
  trackSwipeDeleteClick,
  trackSwipeMarkAsReadClick,
  trackSwipeMoveToArchiveClick,
  trackSwipeRemoveFromArchiveClick,
  trackSwipeUnblockClick,
} from './tracking';
import { openChatActionModal } from '../../actions/chatActionModal';
import { updateChatImages } from '../../actions/chatImages';
import { addChatToSelected, markChatsAsRead, removeChatFromSelected } from '../../actions/chatList';
import { moveToArchive, removeFromArchive, removeFromSpam } from '../../actions/chatOperations';
import { increaseCountClosedChats } from '../../actions/chatWindow';
import { updateMessages } from '../../actions/messages/updateMessages';
import { openUserCard } from '../../actions/userCard/openUserCard';
import { ChatListItem } from '../../components/ChatListItem';
import { EChatListItemSwipeButtonType } from '../../components/ChatListItem/ChatListItemSwipeButton';
import { Preview } from '../../components/ChatListItem/Preview';
import { useCompanionData, useMailingUxfeedbackCampaign } from '../../hooks';
import { chatListSelector, hostTypeSelector, userIdSelector, currentOpenChatIDSelector } from '../../selectors';
import { EChatAction } from '../../types/chatActionModal';
import { TChatInListSchema } from '../../types/chatList';
import { EDeviceType } from '../../types/deviceType';
import { EHostType } from '../../types/hostType';
import { EMessageFileTypes } from '../../types/messageFiles';
import { EOutcomingStatus } from '../../types/messages';
import { TOffer } from '../../types/offer';
import { EChatRole } from '../../types/userData';

type TChatListItemContainerProps = {
  chat: TChatInListSchema;
  openedChatRef?: React.RefObject<HTMLDivElement>;
};

export const ChatListItemContainer: React.FC<TChatListItemContainerProps> = ({ chat, openedChatRef }) => {
  const dispatch = useDispatch();

  const userId = useSelector(userIdSelector);
  const currentOpenChatId = useSelector(currentOpenChatIDSelector);
  const hostType = useSelector(hostTypeSelector);
  const device = useDeviceType();

  const { isChangeListActive, selectedChats, currentRoute } = useSelector(chatListSelector);

  const { lastMessage, data: { offer } = {}, chatId, users } = chat;
  const { type, files, userId: lastMessageUserId } = lastMessage || {};

  const companion = useCompanionData(chatId);
  const previewAvatarNode = usePreviewAvatarNode({ users });
  const lastMessageName = useLastMessageName({ users, lastMessageUserId: lastMessageUserId || 0 });
  const opponent = users.find(user => user.chatRole === EChatRole.Opponent);
  const initiator = users.find(user => user.chatRole === EChatRole.Initiator);
  const { sendMailingUxfeedbackCampaign } = useMailingUxfeedbackCampaign();

  const isUserLastMessage = lastMessage?.userId === userId;

  const status = isUserLastMessage
    ? lastMessage?.status === EMessageV2Status.ReadByRecipient
      ? EOutcomingStatus.Read
      : EOutcomingStatus.Fulfilled
    : undefined;

  const previewImage = React.useMemo(() => {
    if (offer && (offer as TOffer).photos && (offer as TOffer).photos?.length > 0) {
      return ((offer as TOffer).photos.find(p => p.isDefault) || offer.photos[0])?.miniUrl;
    }

    return companion?.avatarUrl;
  }, [offer, companion]);

  const isDesktopFrame = hostType === EHostType.Frame;
  const isMobile = device !== EDeviceType.Desktop;

  const onChatListItemClick = React.useCallback(() => {
    if (isChangeListActive) {
      dispatch(
        selectedChats.includes(chat.chatId) ? removeChatFromSelected(chat.chatId) : addChatToSelected(chat.chatId),
      );
    } else {
      dispatch(updateMessages(chat.chatId));
      dispatch(updateChatImages({ chatId: chat.chatId }));
      trackOpenChatListItemClick(chat.chatId, opponent?.userId, initiator?.userId);

      if (!isDesktopFrame && !isMobile) {
        dispatch(openUserCard(companion.companionId));
      }

      if (currentOpenChatId) {
        dispatch(increaseCountClosedChats());
      }

      if (currentOpenChatId && !isMobile) {
        sendMailingUxfeedbackCampaign();
      }
    }
  }, [
    isChangeListActive,
    dispatch,
    selectedChats,
    chat.chatId,
    opponent?.userId,
    initiator?.userId,
    isDesktopFrame,
    isMobile,
    currentOpenChatId,
    companion.companionId,
    sendMailingUxfeedbackCampaign,
  ]);

  const onSwipeButtonClick = React.useCallback(
    (type: EChatListItemSwipeButtonType) => {
      switch (type) {
        case EChatListItemSwipeButtonType.Archive: {
          dispatch(moveToArchive(chat.chatId));
          trackSwipeMoveToArchiveClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Delete: {
          dispatch(addChatToSelected(chat.chatId));
          dispatch(
            openChatActionModal({
              buttonText: 'Удалить',
              text: 'Точно хотите удалить чат?',
              chatAction: EChatAction.Delete,
            }),
          );
          trackSwipeDeleteClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Read: {
          dispatch(markChatsAsRead([chat.chatId]));
          trackSwipeMarkAsReadClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Return: {
          dispatch(removeFromArchive(chat.chatId));
          trackSwipeRemoveFromArchiveClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Unblock: {
          dispatch(removeFromSpam(chat.chatId));
          trackSwipeUnblockClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Unread: {
          return;
        }
      }
    },
    [dispatch, chat.chatId, offer, opponent?.userId],
  );

  const fileLastMessage = React.useMemo(() => {
    const isFileLastMessage = type === 'textAndFiles';

    if (isFileLastMessage) {
      const type = files?.fileTypes[0] as EMessageFileTypes;
      const name = files?.images?.[0]?.name || files?.documents?.[0]?.name || files?.imagesAsDocuments?.[0]?.name || '';

      return { type, name };
    }

    return undefined;
  }, [files?.documents, files?.fileTypes, files?.images, files?.imagesAsDocuments, type]);

  const isOfferLastMessage = React.useMemo(() => !!lastMessage?.offers?.length, [lastMessage]);

  const props = React.useMemo(
    () => ({
      openedChatRef,
      chatId: chat.chatId,
      checkboxChecked: selectedChats.includes(chat.chatId),
      checkboxShown: isChangeListActive,
      companion,
      isActive: chat.chatId === currentOpenChatId,
      fileLastMessage,
      isOfferLastMessage,
      message: lastMessage?.text || '',
      isMultilineMessage: !offer,
      onClick: onChatListItemClick,
      status,
      unreadCount: chat.unread,
      date: chat.formattedDate,
      onSwipeButtonClick,
      offerInfo: offer?.formattedFullInfo,
      currentRoute,
      lastMessageName,
      previewNode: (
        <Preview
          isOffer={!!offer?.formattedFullInfo}
          previewAvatarNode={previewAvatarNode}
          previewImage={previewImage}
        />
      ),
    }),
    [
      openedChatRef,
      chat.chatId,
      chat.formattedDate,
      chat.unread,
      companion,
      currentOpenChatId,
      currentRoute,
      fileLastMessage,
      isChangeListActive,
      isOfferLastMessage,
      lastMessageName,
      lastMessage?.text,
      offer,
      onChatListItemClick,
      onSwipeButtonClick,
      previewAvatarNode,
      previewImage,
      selectedChats,
      status,
    ],
  );

  return <ChatListItem {...props} />;
};
